import React, {useEffect, useMemo, useState} from "react";
import {deleteById, getAll, getById, update} from "../_request";
import {
    IAxiosGetResponse,
    IFilterTransactionParameterModel,
    IParamType,
    IParamValueType,
    IServiceDto,
    ITransactionParameterDto,
    ITransactionRouteDto
} from "../../interfaces/IUtilities";
import {useToast} from "../../modules/layout/components/ToastContext";
import {Link, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import {ParamTypeText, ParamValueTypeText} from "../../helpers/EnumeratorHelper";
import {FileDiff, FileEarmarkText, Pencil, Trash3} from "react-bootstrap-icons";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import {Field, Form, Formik} from "formik";
import FormikSelect from '../../modules/layout/components/Select/FormikSelect';
import SortDirection from "../../modules/layout/components/SortDirection";
import Loader from "../../modules/layout/components/Loader";
import ConfirmModal from "../../modules/layout/components/ConfirmModal";
import {AxiosResponse} from "axios";
import TransactionRoutesSearch from "../../modules/layout/components/TransactionRoutesSearch";
import Pagination from "../../modules/layout/components/Pagination";


const TransactionParameterList: React.FC = () => {
    const {serviceid , transactionid, } = useParams<{ serviceid: string, transactionid: string }>();
    const {t} = useTranslation();
    const {showToast} = useToast();
    const [service, setService] = useState({} as IServiceDto);
    const [isTransactionSearchOpen, setIsTransactionSearchOpen] = useState(false);
    const [transactionRoute, setTransactionRoute] = useState({} as ITransactionRouteDto);
    const [transactionParameters, setTransactionParameters] = useState([] as ITransactionParameterDto[]);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("paramType");
    const [asc, setAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    let order = asc ? "asc" : "desc";
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedName, setSelectedName] = useState<string | null>(null);

    const initialFormState: IFilterTransactionParameterModel = {
        paramType: undefined,
        paramName: '',
        paramValue: '',
        paramValueType: undefined,
    }
    const [activeFilters, setActiveFilters] = useState<IFilterTransactionParameterModel>(initialFormState);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };

    const fetchServiceDetails = async () => {
        try {
            let result = await getById("/api/services/", serviceid!);
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setService(result.data as IServiceDto);
            }
            setLoading(false);

        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        }
    }

    const fetchTransactionRoute = async () => {
        try {
            let result = await getById(`/api/transactionroutes/`, transactionid!);

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setTransactionRoute(result.data as ITransactionRouteDto);
            } else {
                throw new Error(t('StatusCode', {code: String(respStatus)}));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
        }
    }
    const fetchTransactionParameters = async (filters: IFilterTransactionParameterModel = activeFilters) => {
        try {
            const params = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize },
                { key: 'sortBy', value: sortField },
                { key: 'order', value: order },
            ];
            //if (filters.id) params.push({ key: 'paramType', value: filters.id });
             if (filters.id) {
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                
                if (guidRegex.test(String(filters.id))) {
                    params.push({ key: 'id', value: filters.id });
                } else {
                    showToast(t("IdInvalidFormat"), 'warning');
                    setLoading(false);
                    return; 
                }
            };
            if (filters.paramType) params.push({ key: 'paramType', value: filters.paramType });
            if (filters.paramName) params.push({ key: 'paramName', value: filters.paramName });
            if (filters.paramValue) params.push({ key: 'paramValue', value: filters.paramValue });
            if (filters.paramValueType) params.push({ key: 'paramValueType', value: filters.paramValueType });

            let result = await getAll(`/api/transactionparameters/getall/${transactionid}`,
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data as any;
            dataResults = dataResults["items"] as ITransactionParameterDto[];
            setTotal(result?.data.totalCount);

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setTransactionParameters(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate('/');
        }
    }

    const handleCopyClick = async (sourceid: string[]) => {
        try {
            setLoading(true);
            let result = await update(`/api/transactionparameters/copy/${transactionid}/${sourceid.at(0)}`, {}) as AxiosResponse;
            if (result.status === 200) {
                await fetchTransactionParameters();
                setIsTransactionSearchOpen(false);
                showToast(t('TransactionParametersClonedSuccessfully'), 'success');
            } else if (result.status === 400 && result.data?.errors) {
                setLoading(false);
                for (const key in result.data.errors) {
                    showToast(t(key), 'error');
                }
            } else if (result.status === 403) {
                navigate("/error/403");
            } else if (result.status === 401) {
                navigate("/auth/login");
            } else if (result.status === 500) {
                navigate("/error/500");
            } else {
                throw new Error(t('StatusCode', {code: String(result.status)}));
            }
        } catch (error) {
            setLoading(false);
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        }
    }

    useEffect(() => {
        fetchServiceDetails();
        fetchTransactionRoute();
        fetchTransactionParameters();

    }, [navigate, showToast, page, sortField, asc, pageSize]);

    const handleFilterSearch = async (filters: IFilterTransactionParameterModel) => {
        setPage(1);
        setActiveFilters(filters);
        await fetchTransactionParameters(filters)
    };

    const paramTypeOptions = useMemo(() => [
        { value: '', label: t('All') },
        { value: String(IParamType.HeaderParam), label: ParamTypeText[IParamType.HeaderParam] },
        { value: String(IParamType.AuthParam), label: ParamTypeText[IParamType.AuthParam] },
    ], [t]);

    const paramValueTypeOptions = useMemo(() => [
        { value: '', label: t('All') },
        { value: String(IParamValueType.Bool), label: ParamValueTypeText[IParamValueType.Bool] },
        { value: String(IParamValueType.Int), label: ParamValueTypeText[IParamValueType.Int] },
        { value: String(IParamValueType.Long), label: ParamValueTypeText[IParamValueType.Long] },
        { value: String(IParamValueType.Decimal), label: ParamValueTypeText[IParamValueType.Decimal] },
        { value: String(IParamValueType.String), label: ParamValueTypeText[IParamValueType.String] },
    ], [t]);

    const handleDeleteClick = (id: string, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        setLoading(true);
        if (selectedId) {
            try {
                let result = await deleteById("/api/transactionparameters", selectedId) as AxiosResponse;
                if (result.status === 200) {
                    showToast(t('TransactionParameterDeletedSuccessfully'), 'success');
                    setLoading(false);
                    await fetchTransactionParameters();
                } else if (result.status === 403) {
                    navigate("/error/403");
                } else if (result.status === 401) {
                    navigate("/auth/login");
                } else if (result.status === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                showToast(t('ConnectionError', {error: String(error)}), 'error');
                setLoading(false);
            }

        }
        setShowModal(false);
        setSelectedId(null);
    };

    const handleCancelDelete = () => {
        setShowModal(false);
        setSelectedId(null);
    };

    const handleDuplicationTransactionParameters = (id: string) => {
        setIsTransactionSearchOpen(true);
    }

    const closeTransactionSearch = () => {
        setIsTransactionSearchOpen(false);
    };

    const onErrorTransactionSearch = (error: unknown) => {
        setIsTransactionSearchOpen(false);
        console.log(t('ConnectionError', {error: String(error)}));
        setLoading(false);
        showToast(t('ConnectionError', {error: String(error)}), 'error');
    };


    return (
        <div className="gg-serviceList container mt-4">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('Services'), href: '/services/list'}, // Active page (no href)
                    {title: t('BreadcrumbServiceTransationRoutes', { serviceName: service.name}), href: `/services/${serviceid}/transactions/list`},
                    {title: t('BreadcrumbRouteLabelTransactionParameters', {routeLabel: transactionRoute.routeLabel})} // Active page (no href)
                ]}/>

            <h3 className="mb-3">{t('TransactionParametersList')}</h3>

            {loading && (<Loader />)}

            <div className="mb-3">
                <button className="btn btn-success gg-btn-submit me-2"
                        onClick={() => navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/create`)}>
                    {t('Create')}
                </button>
                <button title={t('CopyTransactionParameters')} className="btn btn-warning me-2"
                        onClick={() => handleDuplicationTransactionParameters(transactionRoute.id)}>
                    {t('CopyParameters')}
                </button>

            </div>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({resetForm, setFieldValue, values}) => (
                        <Form>
                            <div className="row">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                <label className="form-label w-100">{t('Id')}
                                        <Field className="form-control" name="id"/>
                                    </label>
                                </div>
                                                <div className="col-md-6 col-lg-4 col-xl-3">
                                                    <label className="form-label w-100">{t('ParameterType')}
                                                        <FormikSelect
                                                            name="paramType"
                                                            options={paramTypeOptions}
                                                            isClearable
                                                            placeholder={`-- ${t('SelectOption')} --`}
                                                        />
                                                    </label>
                                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ParameterName')}
                                        <Field className="form-control" name="paramName"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ParameterValue')}
                                        <Field className="form-control" name="paramValue"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ParameterValueType')}
                                        <FormikSelect
                                            name="paramValueType"
                                            options={paramValueTypeOptions}
                                            isClearable
                                            placeholder={`-- ${t('SelectOption')} --`}
                                        />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3 align-self-end">
                                    <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                        {t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFormState);
                                                handleFilterSearch(initialFormState)
                                            }}>
                                        {t('Reset')}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                    <tr>
                        <th style={{width: "20%"}} onClick={() => handleSort("paramType")}>{t('ParameterType')}
                            <SortDirection isActive={sortField === 'paramType'} order={order} /></th>
                        <th style={{width: "20%"}} onClick={() => handleSort("paramName")}>{t('ParameterName')}
                            <SortDirection isActive={sortField === 'paramName'} order={order} /></th>
                        <th style={{width: "20%"}} onClick={() => handleSort("paramValue")}>{t('ParameterValue')}
                            <SortDirection isActive={sortField === 'paramValue'} order={order} /></th>
                        <th style={{width: "20%"}} onClick={() => handleSort("paramValueType")}>{t('ParameterValueType')}
                            <SortDirection isActive={sortField === 'paramValueType'} order={order} /></th>
                        <th className="text-wrap" style={{width: "20%"}}>{t('Actions')}</th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {transactionParameters.map(transactionParam => (
                        <tr key={transactionParam.id}>
                            <td>{ParamTypeText[transactionParam.paramType]}</td>
                            <td>{transactionParam.paramName}</td>
                            <td>{transactionParam.paramValue}</td>
                            <td>{ParamValueTypeText[transactionParam.paramValueType]}</td>
                            <td className="text-wrap">
                                <Link title={t('Details')}
                                    to={`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/details/${transactionParam.id}`}
                                    className="btn gg-btn btn-sm me-2">
                                    <FileEarmarkText size={16}/>
                                </Link>
                                <Link title={t('Edit')}
                                    to={`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/edit/${transactionParam.id}`}
                                    className="btn gg-btn btn-sm me-2">
                                    <Pencil size={16} className="text-danger" />
                                </Link>
                                <button title={t('Delete')} className="btn btn-danger btn-sm me-2"
                                        onClick={() => handleDeleteClick(transactionParam.id, transactionParam.paramName)}>
                                    <Trash3 size={16}/>
                                </button>
                                <Link title={t('DataDifferences')}
                                    to={`/services/${transactionRoute.serviceId}/transactions/${transactionRoute.id}/transaction-parameters/datadiff/${transactionParam.id}/list`}
                                    className="btn gg-btn btn-sm">
                                    <FileDiff size={16}/>
                                </Link>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <ConfirmModal
                show={showModal}
                title={t('Delete')}
                message={t('ItemDeletionConfirmation', {selectedName: selectedName})}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                loading={loading}
            />

            <Pagination currentPage={page}
                onPageChange={(page) => { setPage(page) }}
                totalItems={total}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size) }} />


            <TransactionRoutesSearch
                serviceid={serviceid ?? null}
                transactionid={transactionid}
                isTransactionSearchOpen={isTransactionSearchOpen}
                onSelect={handleCopyClick}
                onClose={closeTransactionSearch}
                onError={onErrorTransactionSearch} 
                options={ {
                    title: t('SelectTransactionRoutesToCopyParameters'),
                    showDeleted: false,
                    multipleSelect: false
                } }/>
        </div>
    )
}
export default TransactionParameterList;